import { useQuery } from "@tanstack/react-query"
import { Info } from "lucide-react"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { TONO_FUENTE } from "@/componentes/tesoreria/tipos"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import { cierresDiarios, comoTasa, comoVariacion } from "./formato"
import { GraficoTasa } from "./GraficoTasa"
import type { RegistroTasa, TasaDelPanel } from "./tipos"

const PERIODOS = [7, 30, 90] as const
type Periodo = (typeof PERIODOS)[number]

/** El mismo tope que el servidor (`TOPE_HISTORIAL`). */
const TOPE = 500

/**
 * El historial de una divisa: su gráfico y la tabla de lo que se registró.
 *
 * ⚠ **El periodo es de días de calendario** —7, 30 o 90, como en el 2.0— y lo acota el
 * servidor: la tabla no trae el ledger entero para cortarlo en el navegador.
 *
 * ⚠ **La tabla es la gemela accesible del gráfico, y además lo que se audita**: cada
 * fila con su fecha de valor, cuándo se registró, quién, de dónde vino y el número tal
 * como lo guarda el ledger, al lado de su precio en bolívares.
 *
 * ⚠ **La variación de la tabla es contra la fila ANTERIOR**, no contra el día anterior
 * como en la tarjeta: aquí se lee fila a fila, y el USDT de la tarde se compara con el
 * de la mañana.
 *
 * ⚠ **El selector de periodo es del cristal**, como las pestañas: vive sobre el área de
 * trabajo, y ahí los fondos del tema son un parche.
 */
export function HistorialTasa({ tasa }: { tasa: TasaDelPanel }) {
  const [dias, setDias] = useState<Periodo>(30)
  const historial = useQuery({
    queryKey: ["tasas-historial", tasa.codigo, dias],
    queryFn: () =>
      api.get<RegistroTasa[]>(`/api/v1/tasas/historial?codigo=${tasa.codigo}&dias=${dias}`),
  })
  const registros = historial.data ?? []
  const puntos = cierresDiarios(registros)
  const vista = useRef<HTMLElement | null>(null)

  return (
    <section
      ref={vista}
      aria-label={`Historial del ${tasa.moneda}`}
      className="grid grid-cols-1 gap-3"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold">Historial del {tasa.moneda}</h2>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Bolívares por 1 {tasa.moneda}, al cierre de cada día.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {registros.length > 0 && (
            <Exportar
              titulo={`Historial del ${tasa.moneda}`}
              etiqueta="Exportar historial"
              subtitulo={`Bolívares por 1 ${tasa.moneda}, registros de los últimos ${dias} días`}
              contenido={vista}
            />
          )}
          <fieldset
            className="inline-flex min-w-0 gap-1 rounded-[calc(var(--radius-control)+4px)] p-1"
            style={{
              backgroundColor: "rgb(var(--marco-tinta) / 0.05)",
              boxShadow: "inset 0 0 0 1px rgb(var(--marco-tinta) / 0.1)",
            }}
          >
            <legend className="sr-only">Periodo del historial</legend>
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={dias === p}
                onClick={() => setDias(p)}
                className={`foco-cristal rounded-[var(--radius-control)] px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  dias === p
                    ? "activo-cristal text-[rgb(var(--marco-tinta)/1)]"
                    : "toque-cristal text-[rgb(var(--marco-tinta)/0.68)]"
                }`}
              >
                {p} días
              </button>
            ))}
          </fieldset>
        </div>
      </div>

      {historial.isPending && (
        <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando el historial…
        </p>
      )}
      {historial.isError && (
        <p role="alert" className="tarjeta text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el historial
        </p>
      )}
      {historial.data && registros.length === 0 && (
        <div className="tarjeta">
          <p
            className="flex items-start gap-3 rounded-[var(--radius-control)] border p-3 text-sm"
            style={{
              borderColor: "color-mix(in oklab, var(--info) 35%, var(--border))",
              backgroundColor: "color-mix(in oklab, var(--info) 10%, var(--surface))",
            }}
          >
            <Info
              size={16}
              aria-hidden={true}
              className="mt-0.5 shrink-0"
              style={{ color: "var(--info)" }}
            />
            Sin tasas registradas en los últimos {dias} días.
          </p>
        </div>
      )}

      {registros.length > 0 && (
        <>
          <div className="tarjeta">
            {puntos.length >= 2 ? (
              <GraficoTasa puntos={puntos} moneda={tasa.moneda} />
            ) : (
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                Un solo día con tasa en el periodo: todavía no hay una línea que dibujar.
              </p>
            )}
          </div>

          <Tabla etiqueta={`Registros del ${tasa.moneda}`}>
            <Cabecera>
              <Th derecha>Fecha de valor</Th>
              <Th derecha>Registrada</Th>
              <Th derecha>Bs por 1 {tasa.moneda}</Th>
              <Th derecha>Variación</Th>
              <Th derecha>Guardada, por 1 USD</Th>
              <Th>Fuente</Th>
              <Th>Quién</Th>
            </Cabecera>
            <Cuerpo>
              {registros.map((r) => (
                <Fila key={r.id} data-testid={`registro-${r.id}`}>
                  {/* Sin partir: en un teléfono la tabla se desliza, y una fecha en dos
                      líneas duplica el alto de cada fila. */}
                  <Td derecha numero className="whitespace-nowrap">
                    {comoFecha(r.fecha_valor)}
                  </Td>
                  <Td
                    derecha
                    numero
                    className="whitespace-nowrap"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    {comoFechaHora(r.creado_en)}
                  </Td>
                  <Td
                    derecha
                    numero
                    className="font-medium"
                    data-exportar-valor={r.bs_por_unidad === null ? "" : comoTasa(r.bs_por_unidad)}
                  >
                    {r.bs_por_unidad === null ? (
                      <span style={{ color: "var(--fg-muted)" }}>sin dólar</span>
                    ) : (
                      comoTasa(r.bs_por_unidad)
                    )}
                  </Td>
                  {/* Al Excel sin el «+» ni el menos tipográfico, que no se leen
                      como cifra: «4,06 %» y «-0,22 %». */}
                  <Td
                    derecha
                    numero
                    data-exportar-valor={
                      r.variacion === null ? "" : `${comoTasa(r.variacion, 2)} %`
                    }
                  >
                    {r.variacion === null ? (
                      <span style={{ color: "var(--fg-muted)" }}>—</span>
                    ) : (
                      comoVariacion(r.variacion)
                    )}
                  </Td>
                  <Td derecha numero style={{ color: "var(--fg-muted)" }}>
                    {comoTasa(r.tasa, 6)}
                  </Td>
                  <Td>
                    <Insignia tono={TONO_FUENTE[r.fuente] ?? "neutro"}>{r.fuente}</Insignia>
                  </Td>
                  <Td>
                    {r.registrado_por ?? (
                      <span style={{ color: "var(--fg-muted)" }}>Automática</span>
                    )}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            {registros.length} registro{registros.length === 1 ? "" : "s"} en los últimos {dias}{" "}
            días, del más reciente al más antiguo.
            {registros.length >= TOPE && ` Son los ${TOPE} más recientes.`}
          </p>
        </>
      )}
    </section>
  )
}
